import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAuth } from '../contexts/AuthContext';

const StripeReturn: React.FC = () => {
  usePageTitle('Payment Complete');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, login, signUp } = useAuth();
  const [status, setStatus] = useState<'loading' | 'complete' | 'open' | 'error'>('loading');
  const [customerEmail, setCustomerEmail] = useState('');
  const [userExists, setUserExists] = useState<boolean | null>(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setStatus('error');
      return;
    }

    fetch(`/api/stripe/session-status?session_id=${sessionId}`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to retrieve session');
        return res.json();
      })
      .then((data) => {
        if (data.status === 'complete') {
          setStatus('complete');
          setCustomerEmail(data.customerEmail || '');
          // Clear any pending subscription from localStorage
          localStorage.removeItem('pendingSubscription');

          // Fire purchase conversion event to GTM dataLayer for Google Ads tracking.
          // Dedupe by session_id so reloads don't double-count.
          const firedKey = `gtm_purchase_fired_${sessionId}`;
          if (data.paymentStatus === 'paid' && !localStorage.getItem(firedKey)) {
            const w = window as unknown as { dataLayer?: Record<string, unknown>[] };
            w.dataLayer = w.dataLayer || [];
            w.dataLayer.push({
              event: 'purchase',
              transaction_id: sessionId,
              value: data.amountTotal || 0,
              currency: (data.currency || 'usd').toUpperCase(),
              tier: data.tier || null,
              subscription_id: data.subscriptionId || null,
            });
            try {
              localStorage.setItem(firedKey, '1');
            } catch {
              /* ignore localStorage quota errors */
            }
          }
        } else if (data.status === 'open') {
          setStatus('open');
        } else {
          setStatus('error');
        }
      })
      .catch(() => {
        setStatus('error');
      });
  }, [searchParams]);

  // Check if the customer email exists in Entra ID (only for unauthenticated users)
  useEffect(() => {
    if (status !== 'complete' || isAuthenticated) return;

    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      // No session ID available — default to sign-in
      setUserExists(true);
      return;
    }

    fetch(`/api/auth/check-user?session_id=${encodeURIComponent(sessionId)}`)
      .then((res) => res.json())
      .then((data) => {
        setUserExists(!!data.exists);
      })
      .catch(() => {
        // Default to sign-in on error — MS login will handle account creation if needed
        setUserExists(true);
      });
  }, [status, isAuthenticated, searchParams]);

  const handleSignIn = () => {
    localStorage.setItem('postAuthRedirect', '/dashboard?checkout=success');
    login();
  };

  const handleSignUp = () => {
    localStorage.setItem('postAuthRedirect', '/dashboard?checkout=success');
    signUp();
  };

  if (status === 'loading') {
    return (
      <div className="checkout-page">
        <section className="section">
          <div className="section-container" style={{ textAlign: 'center', padding: '4rem 2rem' }}>
            <h2>Processing your payment...</h2>
            <p>Please wait while we confirm your payment.</p>
          </div>
        </section>
      </div>
    );
  }

  if (status === 'complete') {
    return (
      <div className="checkout-page">
        <section className="section">
          <div className="section-container" style={{ textAlign: 'center', padding: '4rem 2rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>&#10003;</div>
            <h2>Payment Successful!</h2>
            <p style={{ fontSize: '1.1rem', color: '#555', marginBottom: '1.5rem' }}>
              Your subscription is now active. A confirmation email has been sent to{' '}
              <strong>{customerEmail}</strong>.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              {isAuthenticated ? (
                <button
                  className="btn btn-primary"
                  onClick={() => navigate('/dashboard?checkout=success')}
                >
                  Go to Dashboard
                </button>
              ) : userExists === null ? (
                <button className="btn btn-primary" disabled>
                  Loading...
                </button>
              ) : userExists ? (
                <button className="btn btn-primary" onClick={handleSignIn}>
                  Sign into Student Dashboard
                </button>
              ) : (
                <button className="btn btn-primary" onClick={handleSignUp}>
                  Create an Account to Access Student Dashboard
                </button>
              )}
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (status === 'open') {
    return (
      <div className="checkout-page">
        <section className="section">
          <div className="section-container" style={{ textAlign: 'center', padding: '4rem 2rem' }}>
            <h2>Payment Not Completed</h2>
            <p style={{ fontSize: '1.1rem', color: '#555', marginBottom: '1.5rem' }}>
              Your payment was not completed. You can try again by returning to checkout.
            </p>
            <Link to="/checkout" className="btn btn-primary">
              Return to Checkout
            </Link>
          </div>
        </section>
      </div>
    );
  }

  // Error state
  return (
    <div className="checkout-page">
      <section className="section">
        <div className="section-container" style={{ textAlign: 'center', padding: '4rem 2rem' }}>
          <h2>Something Went Wrong</h2>
          <p style={{ fontSize: '1.1rem', color: '#555', marginBottom: '1.5rem' }}>
            We couldn't verify your payment status. If you were charged, your subscription will be
            activated shortly. Please check your email for confirmation.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>
              Go to Dashboard
            </button>
            <Link to="/checkout" className="btn btn-outline">
              Return to Checkout
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StripeReturn;
